import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEdit, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import "../../styles/Form.css";
import "../../styles/AdminDashboard.css";
import Pagination from "../../components/Pagination";
import LeftPanelPersonal from "./LeftPanelPersonal";
import RightPanel from "./RightPanel";
import useAccess from "../../hooks/useAccess";
import StyledSelect from "../../components/StyledSelect";
import DateRangeFilter from "../../components/DateRangeFilter";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import AdminPageHeader from "../../components/AdminPageHeader";
import useClientTableSort, {
  getSortedPage,
} from "../../hooks/useClientTableSort";
import "../../styles/AdministrationPages.css";

const DASHBOARD_TAB_HEADERS = {
  users: { label: "All Associates", icon: "fa-users" },
  drivers: { label: "Driver Licenses", icon: "fa-id-card" },
  healthcards: { label: "Health Cards", icon: "fa-notes-medical" },
  passports: { label: "Passports", icon: "fa-passport" },
  fastcards: { label: "Fast Cards", icon: "fa-bolt" },
  prcards: { label: "PR Cards", icon: "fa-address-card" },
  usvisa: { label: "US Visa", icon: "fa-id-badge" },
  workpermits: { label: "Work Permits", icon: "fa-briefcase" },
  vacation: { label: "Vacation Requests", icon: "fa-calendar-alt" },
  incident: { label: "Incident Reports", icon: "fa-exclamation-triangle" },
  workhistory: { label: "Work History", icon: "fa-building" },
  emergency: { label: "Emergency Contacts", icon: "fa-address-book" },
  directdeposit: { label: "Direct Deposit Files", icon: "fa-university" },
  fuelcards: { label: "Fuel Cards", icon: "fa-gas-pump" },
};

const ADMIN_DASHBOARD_FILTER_STORAGE_KEY = "adminDashboardFilters";

function readSavedDashboardFilters() {
  try {
    const savedFilters = window.sessionStorage.getItem(
      ADMIN_DASHBOARD_FILTER_STORAGE_KEY,
    );
    return savedFilters ? JSON.parse(savedFilters) : {};
  } catch {
    return {};
  }
}

function getSavedFilterValue(savedFilters, key, fallback) {
  return typeof savedFilters?.[key] === "string"
    ? savedFilters[key]
    : fallback;
}

function formatDriverLicenseClass(value) {
  if (Array.isArray(value)) {
    const classes = value
      .map((item) => String(item).trim())
      .filter(Boolean);
    return classes.length ? classes.join(", ") : "-";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "-";

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return formatDriverLicenseClass(parsed);
      }
    } catch {
      // Fall back to plain string formatting below.
    }

    return trimmed.includes(",")
      ? trimmed
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .join(", ") || "-"
      : trimmed;
  }

  return value == null ? "-" : String(value);
}

function AdminDashboard() {
  // 🌐 API & Constants
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // 📦 React Router & Navigation
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const savedFilters = useRef(readSavedDashboardFilters()).current;
  const initialTab =
    queryParams.get("tab") ||
    getSavedFilterValue(savedFilters, "tab", "users");

  // 📦 State: UI & Control
  const [tab, setTab] = useState(initialTab);
  const tabHeader = DASHBOARD_TAB_HEADERS[tab] || DASHBOARD_TAB_HEADERS.users;
  const [slidePanelVisible, setSlidePanelVisible] = useState(false);
  const [slideDirection, setSlideDirection] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // 🔍 State: Filters & Query (legacy name-based from URL)
  const [empCategoryFilter, setEmpCategoryFilter] = useState(() =>
    getSavedFilterValue(savedFilters, "empCategoryFilter", "All"),
  );
  const [terminalFilter, setTerminalFilter] = useState(() =>
    getSavedFilterValue(savedFilters, "terminalFilter", "All"),
  );
  const [provinceFilter, setProvinceFilter] = useState(() =>
    getSavedFilterValue(savedFilters, "provinceFilter", "All"),
  );
  const [statusFilter, setStatusFilter] = useState(() =>
    getSavedFilterValue(savedFilters, "statusFilter", "Associate"),
  );
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() =>
    getSavedFilterValue(savedFilters, "searchQuery", ""),
  );
  const [statusChangeFrom, setStatusChangeFrom] = useState(() =>
    getSavedFilterValue(savedFilters, "statusChangeFrom", ""),
  );
  const [statusChangeTo, setStatusChangeTo] = useState(() =>
    getSavedFilterValue(savedFilters, "statusChangeTo", ""),
  );

  // 🗃️ State: Data Collections (restore all so tabs work)
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [driverInfos, setDriverInfos] = useState([]);
  const [healthCardInfos, setHealthCardInfos] = useState([]);
  const [passportInfos, setPassportInfos] = useState([]);
  const [fastCardInfos, setFastCardInfos] = useState([]);
  const [prCardInfos, setPrCardInfos] = useState([]);
  const [usVisaInfos, setUsVisaInfos] = useState([]);
  const [workPermitInfos, setWorkPermitInfos] = useState([]);
  const [vacationInfos, setVacationInfos] = useState([]);
  const [workHistoryInfos, setWorkHistoryInfos] = useState([]);
  const [emergencyInfos, setEmergencyInfos] = useState([]);
  const [directDepositInfos, setDirectDepositInfos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgUnit, setOrgUnit] = useState([]);
  const [countries, setCountries] = useState([]);
  const [vacationTypes, setVacationTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [driverManagers, setDriverManagers] = useState([]);
  const [fuelCardInfos, setFuelCardInfos] = useState([]);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // 🚫 TAB-LEVEL ACCESS CONTROL — prevents manual URL access
  const TAB_PERMISSIONS = {
    drivers: ["AdminDashboard", "Driver License", "view"],
    healthcards: ["AdminDashboard", "Health Card", "view"],
    fastcards: ["AdminDashboard", "Fast Card", "view"],
    prcards: ["AdminDashboard", "PR Card", "view"],
    usvisa: ["AdminDashboard", "US Visa", "view"],
    passports: ["AdminDashboard", "Passport", "view"],
    workpermits: ["AdminDashboard", "Work Permit", "view"],
    workhistory: ["AdminDashboard", "Work History", "view"],
    vacation: ["AdminDashboard", "Vacation", "view"],
    emergency: ["AdminDashboard", "Emergency Contact", "view"],
    directdeposit: ["AdminDashboard", "Direct Deposit", "view"],
    fuelcards: ["AdminDashboard", "Fuel Card", "view"],
  };

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    users: {
      create: hasPermission("AdminDashboard", "User", "edit"),
      edit: hasPermission("AdminDashboard", "User", "edit"),
      view: hasPermission("AdminDashboard", "User", "view"),
      assign: hasPermission("AdminDashboard", "User", "edit"),
      delete: hasPermission("AdminDashboard", "User", "delete"),
    },
    drivers: {
      edit: hasPermission("AdminDashboard", "Driver License", "edit"),
    },
    healthcards: {
      edit: hasPermission("AdminDashboard", "Health Card", "edit"),
    },
    passports: {
      edit: hasPermission("AdminDashboard", "Passport", "edit"),
    },
    fastcards: {
      edit: hasPermission("AdminDashboard", "Fast Card", "edit"),
    },
    prcards: {
      edit: hasPermission("AdminDashboard", "PR Card", "edit"),
    },
    usvisa: {
      edit: hasPermission("AdminDashboard", "US Visa", "edit"),
    },
    workpermits: {
      edit: hasPermission("AdminDashboard", "Work Permit", "edit"),
    },
    workhistory: {
      edit: hasPermission("AdminDashboard", "Work History", "edit"),
    },
    vacation: {
      edit: hasPermission("AdminDashboard", "Vacation", "edit"),
      delete: hasPermission("AdminDashboard", "Vacation", "delete"),
    },
    emergency: {
      edit: hasPermission("AdminDashboard", "Emergency Contact", "edit"),
    },
    directdeposit: {
      edit: hasPermission("AdminDashboard", "Direct Deposit", "edit"),
    },
    fuelcards: {
      edit: hasPermission("AdminDashboard", "Fuel Card", "edit"),
    },
  };

  // 📄 Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const dashboardTableRef = useRef(null);
  const dashboardSortConfig = useClientTableSort(
    dashboardTableRef,
    () => setCurrentPage(1),
    tab,
  );
  const hasInitializedFilterUrl = useRef(false);
  const lastDashboardFilterSearch = useRef(location.search);
  const itemsPerPage = 20;

  // 🔄 Stable helper to fetch users (role = 2 only)
  const loadUsers = useCallback(async () => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get(`${BASE_URL}/api/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const onlyRole2 = Array.isArray(data)
      ? data.filter((u) => Number(u.role) === 2 && Number(u.re_applied) === 0)
      : [];
    setUsers(onlyRole2);
  }, [BASE_URL]);

  const getPositionNumber = useCallback(
    (positionFull) => {
      const match = positions.find(
        (p) => String(p.p_id) === String(positionFull),
      );
      return match?.p_position_number || positionFull || "-";
    },
    [positions],
  );

  // Initial fetches
  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const loadCustomTaskAdminList = (taskKey, setter) => {
      axios
        .get(`${BASE_URL}/api/custom-tasks/admin/list/${taskKey}`, { headers })
        .then((res) => setter(res.data))
        .catch((err) =>
          console.error(`Failed to load ${taskKey} admin list`, err),
        );
    };

    // Users
    loadUsers();

    axios
      .get(`${BASE_URL}/api/positions`, { headers })
      .then((res) => setPositions(res.data || []))
      .catch((err) => console.error("Failed to load positions", err));

    // Admin tools datasets (restore so tabs work)
    axios
      .get(`${BASE_URL}/api/driverlicense/admin/all`)
      .then((res) => setDriverInfos(res.data));
    loadCustomTaskAdminList("health_cards", setHealthCardInfos);
    loadCustomTaskAdminList("fast_card", setFastCardInfos);
    loadCustomTaskAdminList("fuel_card", setFuelCardInfos);
    loadCustomTaskAdminList("passport", setPassportInfos);
    loadCustomTaskAdminList("pr_card", setPrCardInfos);
    loadCustomTaskAdminList("us_visa", setUsVisaInfos);
    loadCustomTaskAdminList("work_permit", setWorkPermitInfos);
    loadCustomTaskAdminList("emergency_contact_information", setEmergencyInfos);
    loadCustomTaskAdminList(
      "direct_deposit_authorization",
      setDirectDepositInfos,
    );
    axios
      .get(`${BASE_URL}/api/vacationform/admin/all`)
      .then((res) => setVacationInfos(res.data));
    axios
      .get(`${BASE_URL}/api/work-history/admin/all`)
      .then((res) => setWorkHistoryInfos(res.data));
  }, [BASE_URL, navigate, location.state, loadUsers]);

  // Explicit URL filters take precedence over the remembered filters.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.has("tab")) setTab(qs.get("tab") || "users");
    if (qs.has("filter")) setEmpCategoryFilter(qs.get("filter") || "All");
    if (qs.has("status")) setStatusFilter(qs.get("status") || "Associate");
  }, [location.search]);

  // Keep filters while navigating to a user/task and back in the same tab.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        ADMIN_DASHBOARD_FILTER_STORAGE_KEY,
        JSON.stringify({
          tab,
          searchQuery,
          empCategoryFilter,
          terminalFilter,
          provinceFilter,
          statusFilter,
          statusChangeFrom,
          statusChangeTo,
        }),
      );
    } catch {
      // Filtering should still work when browser storage is unavailable.
    }
  }, [
    tab,
    searchQuery,
    empCategoryFilter,
    terminalFilter,
    provinceFilter,
    statusFilter,
    statusChangeFrom,
    statusChangeTo,
  ]);

  // Keep the current history entry aligned with the visible filters so the
  // browser Back button restores the same result instead of stale defaults.
  useEffect(() => {
    if (!hasInitializedFilterUrl.current) {
      hasInitializedFilterUrl.current = true;
      return;
    }

    // Let incoming navigation filters update state before writing the URL.
    if (lastDashboardFilterSearch.current !== location.search) {
      lastDashboardFilterSearch.current = location.search;
      return;
    }

    const qs = new URLSearchParams(location.search);
    const setOrDelete = (key, value) => {
      if (value) qs.set(key, value);
      else qs.delete(key);
    };

    qs.set("tab", tab);
    qs.set("filter", empCategoryFilter);
    qs.set("terminal", terminalFilter);
    qs.set("province", provinceFilter);
    qs.set("status", statusFilter);
    setOrDelete("search", searchQuery);
    setOrDelete("statusFrom", statusChangeFrom);
    setOrDelete("statusTo", statusChangeTo);

    const nextSearch = qs.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate(
        { pathname: location.pathname, search: `?${nextSearch}` },
        { replace: true },
      );
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    tab,
    searchQuery,
    empCategoryFilter,
    terminalFilter,
    provinceFilter,
    statusFilter,
    statusChangeFrom,
    statusChangeTo,
  ]);

  // Dropdown data
  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    axios
      .get(`${BASE_URL}/api/dropdowns/categories`, { headers })
      .then((res) => setCategories(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/org-units`, { headers })
      .then((res) => setOrgUnit(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/user-status`, { headers })
      .then((res) => setStatusOptions(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/countries`, { headers })
      .then((res) => setCountries(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/vacation-type`, { headers })
      .then((res) => setVacationTypes(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/recruiters`, { headers })
      .then((res) => setRecruiters(res.data));
    axios
      .get(`${BASE_URL}/api/dropdowns/driver-managers`, { headers })
      .then((res) => setDriverManagers(res.data));
  }, [BASE_URL]);

  // Reset page on search/filter
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    empCategoryFilter,
    terminalFilter,
    statusFilter,
    statusChangeFrom,
    statusChangeTo,
  ]);

  const handleDelete = (id, type) => {
    let url = "";
    if (type === "user") url = `/api/users/${id}`;
    else if (type === "vacation") url = `/api/vacationform/${id}`;

    if (!url) return;

    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      axios
        .delete(`${BASE_URL}${url}`)
        .then(() => {
          alert(`${type} Deleted successfully`);
          if (type === "user") {
            loadUsers();
          } else if (type === "vacation") {
            axios
              .get(`${BASE_URL}/api/vacationform/admin/all`)
              .then((res) => setVacationInfos(res.data));
          }
        })
        .catch(() => alert(`Failed to delete ${type}`));
    }
  };

  const handleEdit = (id, type, wpId) => {
    if (type === "user") navigate(`/userhome/${id}`);
    else if (type === "health_cards") navigate(`/task/health_cards/${id}`);
    else if (type === "pr_card") navigate(`/task/pr_card/${id}`);
    else if (type === "us_visa") navigate(`/task/us_visa/${id}`);
    else if (type === "fuel_card") navigate(`/task/fuel_card/${id}`);
    else if (type === "fast_card") navigate(`/task/fast_card/${id}`);
    else if (type === "passport") navigate(`/task/passport/${id}`);
    else if (type === "workpermit") navigate(`/task/work_permit/${id}`);
    else if (type === "emergency")
      navigate(`/task/emergency_contact_information/${id}`);
    else if (type === "directdeposit")
      navigate(`/task/direct_deposit_authorization/${id}`);
    else navigate(`/edit-${type}/${id}`);
  };

  // Helpers to map legacy name filters to IDs for comparison
  const getCategoryIdByName = (name) => {
    if (!name || name === "All") return null;
    const cat = categories.find((c) => c.c_name === name);
    return cat ? String(cat.c_id) : null;
  };
  const getCategoryIdsByFilter = (filterValue) => {
    if (!filterValue || filterValue === "All") return [];

    return filterValue
      .split(",")
      .map((name) => getCategoryIdByName(name.trim()))
      .filter(Boolean);
  };
  const getStatusIdByName = (name) => {
    if (!name || name === "All") return null;
    const s = statusOptions.find((s) => s.s_TranslatedText === name);
    return s ? String(s.s_id) : null;
  };

  // Display helpers
  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.c_id === parseInt(categoryId));
    return cat ? cat.c_translatedText : categoryId;
  };
  const getOrgUnit = (orgUnitId) => {
    const ou = orgUnit.find((ou) => ou.ou_id === parseInt(orgUnitId));
    return ou ? ou.ou_name : orgUnitId;
  };
  const getCountryName = (countryId) => {
    const cn = countries.find((cn) => cn.id === parseInt(countryId));
    return cn ? cn.country_name : countryId;
  };
  const getVacationTypeName = (typeId) => {
    const vt = vacationTypes.find((vt) => vt.vt_id === parseInt(typeId));
    return vt ? vt.vt_name : typeId;
  };
  const getRecruiterName = (userId) => {
    const m = recruiters.find((m) => m.value === parseInt(userId));
    return m ? m.label : userId;
  };
  const getDriverManagerName = (userId) => {
    const dm = driverManagers.find((dm) => dm.value === parseInt(userId));
    return dm ? dm.label : userId;
  };

  // Unified filter — works for legacy path filters (names)
  const applySearchFilter = (
    data,
    categoryField = "emp_category",
    statusField = "status",
  ) => {
    const categoryIds = getCategoryIdsByFilter(empCategoryFilter);
    const categoryNames = empCategoryFilter
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
    const statusId = getStatusIdByName(statusFilter);
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const phoneSearchDigits = /^[\d\s()+.-]+$/.test(searchQuery)
      ? searchQuery.replace(/\D/g, "")
      : "";

    return data.filter((item) => {
      const matchesText = Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(normalizedSearch),
      );
      const matchesPhone =
        phoneSearchDigits.length >= 3 &&
        [item.mobile, item.home_phone].some((phone) =>
          String(phone || "")
            .replace(/\D/g, "")
            .includes(phoneSearchDigits),
        );
      const matchesSearch = matchesText || matchesPhone;

      let categoryMatch = true;
      if (tab === "workhistory") {
        categoryMatch =
          empCategoryFilter === "All" ||
          categoryNames.includes(item[categoryField]);
      } else {
        categoryMatch =
          empCategoryFilter === "All" ||
          categoryIds.includes(item[categoryField]?.toString());
      }

      const statusMatch =
        tab !== "users" ||
        statusFilter === "All" ||
        item[statusField]?.toString() === statusId;

      const terminalMatch =
        tab !== "users" ||
        terminalFilter === "All" ||
        item.org_unit?.toString() === terminalFilter;

      const statusChangeDate = item.status_change_date || "";
      const statusChangeFromMatch =
        tab !== "users" ||
        !statusChangeFrom ||
        (statusChangeDate && statusChangeDate >= statusChangeFrom);
      const statusChangeToMatch =
        tab !== "users" ||
        !statusChangeTo ||
        (statusChangeDate && statusChangeDate <= statusChangeTo);

      const provinceMatch =
        tab !== "drivers" ||
        provinceFilter === "All" ||
        item.dl_province === provinceFilter;

      return (
        matchesSearch &&
        categoryMatch &&
        statusMatch &&
        terminalMatch &&
        statusChangeFromMatch &&
        statusChangeToMatch &&
        provinceMatch
      );
    });
  };

  // Fetch full user when opening panels
  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    } catch (err) {
      console.error("❌ Failed to fetch full user info:", err);
      return null;
    }
  };

  const filteredUsers = applySearchFilter(users);

  const getDashboardSortAccessors = (activeTab) => {
    const accessorsByTab = {
      users: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) =>
          `${row.position} - ${getPositionNumber(row.position_full)} - ${
            getOrgUnit(row.org_unit) || "-"
          }`,
        (row) => getCategoryName(row.emp_category),
        (row) =>
          row.driver_manager && row.driver_manager.toString().trim() !== ""
            ? getDriverManagerName(row.driver_manager)
            : getRecruiterName(row.emp_recruiter) || "-",
        (row) => getCountryName(row.country),
        (row) => row.days_since_status_change,
      ],
      drivers: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => formatDriverLicenseClass(row.dl_class),
        (row) => row.dl_expdate,
        (row) => row.dl_province,
      ],
      healthcards: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.expiry_date,
        (row) => row.attachment_count,
      ],
      fastcards: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.card_number,
        (row) => row.expiry_date,
        (row) => row.attachment_count,
      ],
      passports: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.expiry_date,
        (row) => row.issue_country,
      ],
      prcards: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.expiry_date,
        (row) => row.attachment_count,
      ],
      usvisa: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.expiry_date,
        (row) => row.attachment_count,
      ],
      workpermits: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.expiry_date,
      ],
      workhistory: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.wh_type,
        (row) => row.wh_name,
        (row) => row.wh_country,
        (row) => row.wh_datefrom,
        (row) => row.wh_dateto,
        (row) => row.wh_position,
      ],
      vacation: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => getVacationTypeName(row.vf_type),
        (row) => getRecruiterName(row.vf_manager),
        (row) => row.vf_from_date,
        (row) => row.vf_to_date,
        (row) => row.vf_form_original,
        (row) => row.vf_submitted_date,
      ],
      emergency: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.ec1_name,
        (row) => row.ec1_relationship,
        (row) => row.ec1_phone,
        (row) => row.ec1_email,
      ],
      directdeposit: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.dd_institution_name,
        (row) => row.dd_account_type,
        (row) => row.dd_associate_name,
        (row) => row.attachment_count,
      ],
      fuelcards: [
        (row) => row.userid_no,
        (row) => row.full_name,
        (row) => row.card_number,
        (row) => row.expiry_date,
        (row) => row.attachment_count,
      ],
    };

    return accessorsByTab[activeTab] || [];
  };

  const getDashboardPage = (data) =>
    getSortedPage(
      data,
      dashboardSortConfig,
      getDashboardSortAccessors(tab),
      currentPage,
      itemsPerPage,
    );

  const empCategoryOptions =
    tab === "workhistory"
      ? [
          { value: "All", label: "All" },
          { value: "Working", label: "Working" },
          { value: "School", label: "School" },
          { value: "Driving School", label: "Driving School" },
          { value: "Military", label: "Military" },
          { value: "Unemployed", label: "Unemployed" },
        ]
      : [
          { value: "All", label: "All" },
          ...categories.map((cat) => ({
            value: cat.c_name,
            label: cat.c_name,
          })),
        ];

  const statusSelectOptions = [
    { value: "All", label: "All" },
    ...statusOptions.map((status) => ({
      value: status.s_TranslatedText,
      label: status.s_TranslatedText,
    })),
  ];

  const terminalSelectOptions = [
    { value: "All", label: "All Terminals" },
    ...orgUnit.map((terminal) => ({
      value: String(terminal.ou_id),
      label: terminal.ou_name,
    })),
  ];

  const provinceSelectOptions = [
    { value: "All", label: "All Provinces" },
    ...[
      ...new Set(driverInfos.map((driver) => driver.dl_province).filter(Boolean)),
    ].map((province) => ({
      value: province,
      label: province,
    })),
  ];

  // Check permission for the current tab
  let canViewTab = true;
  if (TAB_PERMISSIONS[tab]) {
    const [page, resource, action] = TAB_PERMISSIONS[tab];
    canViewTab = hasPermission(page, resource, action);
  }

  if (!canViewTab) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">Access Denied</h4>
        <p>You do not have permission to view this section.</p>
      </div>
    );
  }

  return (
    <div className="admin-standard-page admin-dashboard-page">
      <AdminPageHeader
        icon={`fas ${tabHeader.icon}`}
        title={tabHeader.label}
        description="Review and manage associate records and administrative documents."
        actions={tab === "users" && PERMISSIONS.users.create && (
          <button
            className="btn btn-primary dashboard-add-button"
            onClick={() => navigate("/form")}
          >
            Add New User <i className="fas fa-plus" aria-hidden="true" />
          </button>
        )}
      />

      <section className="admin-standard-workspace">
        <div className="admin-standard-workspace-body">

      <ReportFilterPanel>
      <div className="row align-items-end nw-filter-bar admin-dashboard-filter-grid">
        <div className="col-auto">
          <label className="admin-standard-filter-field">
          <span>Search</span>
          <input
            type="search"
            className="form-control"
            placeholder="Search by name, ID, phone, or any field..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: "250px" }}
          />
          </label>
        </div>

        {["users", "workhistory"].includes(tab) && (
          <div className="col-auto">
            <label className="admin-standard-filter-field"><span>Category</span>
            <StyledSelect
              value={empCategoryFilter}
              selectedLabel={empCategoryFilter.includes(",") ? "Multiple categories" : undefined}
              options={empCategoryOptions}
              onChange={setEmpCategoryFilter}
              minWidth={310}
              ariaLabel="Filter by category"
            />
            </label>
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <label className="admin-standard-filter-field"><span>Org Unit</span>
            <StyledSelect
              value={terminalFilter}
              options={terminalSelectOptions}
              onChange={setTerminalFilter}
              minWidth={220}
              ariaLabel="Filter by terminal"
            />
            </label>
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <label className="admin-standard-filter-field"><span>Status</span>
            <StyledSelect
              value={statusFilter}
              options={statusSelectOptions}
              onChange={setStatusFilter}
              minWidth={180}
              ariaLabel="Filter by status"
            />
            </label>
          </div>
        )}

        {tab === "users" && (
          <div className="col-auto">
            <DateRangeFilter
              fromValue={statusChangeFrom}
              toValue={statusChangeTo}
              onFromChange={setStatusChangeFrom}
              onToChange={setStatusChangeTo}
              label="Status change date"
            />
          </div>
        )}

        {tab === "drivers" && (
          <div className="col-auto">
            <label className="admin-standard-filter-field"><span>Province</span>
            <StyledSelect
              value={provinceFilter}
              options={provinceSelectOptions}
              onChange={setProvinceFilter}
              minWidth={200}
              ariaLabel="Filter by province"
            />
            </label>
          </div>
        )}
      </div>
      </ReportFilterPanel>

      {/* USERS */}
      {tab === "users" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">Employee ID</th>
                <th data-mobile-column="name">Full Name</th>
                <th data-mobile-column="detail">Position</th>
                <th data-mobile-column="secondary">Category</th>
                <th data-mobile-column="secondary">Assignee</th>
                <th data-mobile-column="secondary">Country</th>
                <th data-mobile-column="secondary" className="text-center">Days Since Updated</th>
                <th data-mobile-column="actions" className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(filteredUsers).map((u) => (
                <tr key={u.id}>
                  <td data-mobile-label="Employee ID" data-mobile-column="id">{u.userid_no}</td>
                  <td data-mobile-label="Full Name" data-mobile-column="name"
                    onMouseEnter={() => {
                      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                        return;
                      }
                      const timer = setTimeout(async () => {
                        const fullUser = await fetchUserDetails(u.id);
                        if (fullUser) {
                          setSelectedUser(fullUser);
                          setSlideDirection("right");
                          setSlidePanelVisible(true);
                        }
                      }, 1500);
                      u._hoverTimer = timer;
                    }}
                    onMouseLeave={() => {
                      if (u._hoverTimer) clearTimeout(u._hoverTimer);
                      u._hoverTimer = null;
                    }}
                    onClick={async () => {
                      const fullUser = await fetchUserDetails(u.id);
                      if (fullUser) {
                        setSelectedUser(fullUser);
                        setSlideDirection(
                          window.matchMedia("(hover: hover) and (pointer: fine)")
                            .matches
                            ? "left"
                            : "right",
                        );
                        setSlidePanelVisible(true);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      color: "blue",
                      textDecoration: "underline",
                    }}
                  >
                    {u.full_name}
                  </td>

                  <td data-mobile-label="Position" data-mobile-column="detail">
                    {u.position} - {getPositionNumber(u.position_full)} -{" "}
                    {getOrgUnit(u.org_unit) || "-"}
                  </td>
                  <td data-mobile-label="Category" data-mobile-column="secondary">{getCategoryName(u.emp_category)}</td>
                  <td data-mobile-label="Assignee" data-mobile-column="secondary">
                    {u.driver_manager &&
                    u.driver_manager.toString().trim() !== ""
                      ? getDriverManagerName(u.driver_manager)
                      : getRecruiterName(u.emp_recruiter) || "-"}
                  </td>
                  <td data-mobile-label="Country" data-mobile-column="secondary">{getCountryName(u.country)}</td>
                  <td data-mobile-label="Days Since Updated" data-mobile-column="secondary"
                    className="text-center"
                    title={
                      u.status_change_date
                        ? `Status last changed on ${u.status_change_date}`
                        : "No status change history"
                    }
                  >
                    {u.days_since_status_change ?? "-"}
                  </td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => {
                        const isAdmin = Number(u.role) === 1;
                        const isAssociate = Number(u.status) === 1; // status 1
                        const isCandidate = Number(u.status) === 3; // status 3

                        if (isAssociate) {
                          // 1) Associates → go to that user's userHome
                          navigate(`/userhome/${u.id}`);
                          return;
                        }

                        if (isAdmin) {
                          // 3) Admins → edit page; Return should go back to /admin-users
                          navigate(`/edit-user/${u.id}`, {
                            state: { returnTo: "/admin-users" },
                          });
                          return;
                        }

                        if (isCandidate) {
                          // 2) Candidates → go to userHome
                          navigate(`/userhome/${u.id}`);
                          return;
                        }

                        // sensible default
                        navigate(`/userhome/${u.id}`);
                      }}
                      disabled={!PERMISSIONS.users.view}
                    >
                      <FaEye />
                    </button>

                    {PERMISSIONS.users.delete && (
                      <button
                        className="btn btn-danger btn-sm btn-action"
                        onClick={() => handleDelete(u.id, "user")}
                      >
                        <i className="fas fa-trash" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* DRIVER LICENSES */}
      {tab === "drivers" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="secondary">Class Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Province</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(driverInfos)).map((d) => (
                <tr key={d.dl_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{d.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{d.full_name || "-"}</td>
                  <td data-mobile-label="Class Name" data-mobile-column="secondary">{formatDriverLicenseClass(d.dl_class)}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {d.dl_expdate ? dayjs(d.dl_expdate).format("YYYY-MM-DD") : "-"}
                  </td>
                  <td data-mobile-label="Province" data-mobile-column="secondary">{d.dl_province || "-"}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(d.dl_userid, "driver")}
                      disabled={!PERMISSIONS.drivers.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(driverInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* HEALTH CARDS */}
      {tab === "healthcards" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(healthCardInfos)).map((h) => (
                <tr key={h.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{h.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{h.full_name || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {h.expiry_date
                      ? dayjs(h.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Files" data-mobile-column="secondary">{h.attachment_count || 0}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(h.user_id, "health_cards")}
                      disabled={!PERMISSIONS.healthcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(healthCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Fast Cards */}
      {tab === "fastcards" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="secondary">Card Number</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(fastCardInfos)).map((f) => (
                <tr key={f.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{f.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{f.full_name || "-"}</td>
                  <td data-mobile-label="Card Number" data-mobile-column="secondary">{f.card_number || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {f.expiry_date
                      ? dayjs(f.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Files" data-mobile-column="secondary">{f.attachment_count || 0}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(f.user_id, "fast_card")}
                      disabled={!PERMISSIONS.fastcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(fastCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Passports */}
      {tab === "passports" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Issue Country</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(passportInfos)).map((p) => (
                <tr key={p.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{p.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{p.full_name || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {p.expiry_date
                      ? dayjs(p.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Issue Country" data-mobile-column="secondary">{p.issue_country || "-"}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(p.user_id, "passport")}
                      disabled={!PERMISSIONS.passports.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(passportInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* PR Cards */}
      {tab === "prcards" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(prCardInfos)).map((p) => (
                <tr key={p.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{p.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{p.full_name || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {p.expiry_date
                      ? dayjs(p.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Files" data-mobile-column="secondary">{p.attachment_count || 0}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(p.user_id, "pr_card")}
                      disabled={!PERMISSIONS.prcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(prCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* US Visa */}
      {tab === "usvisa" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(usVisaInfos)).map((v) => (
                <tr key={v.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{v.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{v.full_name || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {v.expiry_date
                      ? dayjs(v.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Files" data-mobile-column="secondary">{v.attachment_count || 0}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(v.user_id, "us_visa")}
                      disabled={!PERMISSIONS.usvisa.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(usVisaInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Work Permits */}
      {tab === "workpermits" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(workPermitInfos)).map((w) => (
                <tr key={w.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{w.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{w.full_name || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {w.expiry_date
                      ? dayjs(w.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(w.user_id, "workpermit")}
                      disabled={!PERMISSIONS.workpermits.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(workPermitInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* WORK HISTORY (list view — the slide panel shows only Working) */}
      {tab === "workhistory" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="secondary">Type</th>
                <th data-mobile-column="detail">Company</th>
                <th data-mobile-column="secondary">Country</th>
                <th data-mobile-column="secondary">From</th>
                <th data-mobile-column="secondary">To</th>
                <th data-mobile-column="secondary">Position</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(
                applySearchFilter(workHistoryInfos, "wh_type"),
              ).map((w) => (
                <tr key={w.wh_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{w.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{w.full_name || "-"}</td>
                  <td data-mobile-label="Type" data-mobile-column="secondary">{w.wh_type || "-"}</td>
                  <td data-mobile-label="Company" data-mobile-column="detail">{w.wh_name || "-"}</td>
                  <td data-mobile-label="Country" data-mobile-column="secondary">{w.wh_country || "-"}</td>
                  <td data-mobile-label="From" data-mobile-column="secondary">{dayjs(w.wh_datefrom).format("YYYY-MM-DD") || "-"}</td>
                  <td data-mobile-label="To" data-mobile-column="secondary">
                    {w.wh_dateto
                      ? dayjs(w.wh_dateto).format("YYYY-MM-DD")
                      : "Present" || "-"}
                  </td>
                  <td data-mobile-label="Position" data-mobile-column="secondary">{w.wh_position || "-"}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(w.wh_userid, "workhistory")}
                      disabled={!PERMISSIONS.workhistory.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(workHistoryInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* VACATION FORMS */}
      {tab === "vacation" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Type</th>
                <th data-mobile-column="secondary">Manager</th>
                <th data-mobile-column="secondary">From Date</th>
                <th data-mobile-column="secondary">To Date</th>
                <th data-mobile-column="secondary">File Name</th>
                <th data-mobile-column="secondary">Submitted Date</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(vacationInfos)).map((v) => (
                <tr key={v.vf_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{v.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{v.full_name || "-"}</td>
                  <td data-mobile-label="Type" data-mobile-column="detail"><span className="mobile-vacation-type">{getVacationTypeName(v.vf_type) || "-"}</span><span className="mobile-table-detail"><i className="far fa-calendar" aria-hidden="true" /> {dayjs(v.vf_from_date).format("MMM D, YYYY")} ? {dayjs(v.vf_to_date).format("MMM D, YYYY")}</span></td>
                  <td data-mobile-label="Manager" data-mobile-column="secondary">{getRecruiterName(v.vf_manager) || "-"}</td>
                  <td data-mobile-label="From Date" data-mobile-column="secondary">{dayjs(v.vf_from_date).format("YYYY-MM-DD") || "-"}</td>
                  <td data-mobile-label="To Date" data-mobile-column="secondary">{dayjs(v.vf_to_date).format("YYYY-MM-DD") || "-"}</td>
                  <td data-mobile-label="File Name" data-mobile-column="secondary">{v.vf_form_original || "-"}</td>
                  <td data-mobile-label="Submitted Date" data-mobile-column="secondary">
                    {dayjs(v.vf_submitted_date).format("YYYY-MM-DD") || "-"}
                  </td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    {/* ✏️ Edit permission */}
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(v.vf_userid, "vacation")}
                      disabled={!PERMISSIONS.users.edit}
                    >
                      <FaEdit />
                    </button>

                    {/* 🗑️ Delete permission */}
                    {PERMISSIONS.vacation.delete && (
                      <button
                        className="btn btn-danger btn-sm btn-action"
                        onClick={() => handleDelete(v.vf_id, "vacation")}
                      >
                        <i className="fas fa-trash" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(vacationInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* EMERGENCY CONTACT */}
      {tab === "emergency" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Contact Name</th>
                <th data-mobile-column="secondary">Relationship</th>
                <th data-mobile-column="secondary">Phone</th>
                <th data-mobile-column="secondary">Email</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(emergencyInfos)).map((ec) => (
                <tr key={ec.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{ec.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{ec.full_name || "-"}</td>
                  <td data-mobile-label="Contact Name" data-mobile-column="detail">{ec.ec1_name || "-"}</td>
                  <td data-mobile-label="Relationship" data-mobile-column="secondary">{ec.ec1_relationship || "-"}</td>
                  <td data-mobile-label="Phone" data-mobile-column="secondary">{ec.ec1_phone || "-"}</td>
                  <td data-mobile-label="Email" data-mobile-column="secondary">{ec.ec1_email || "-"}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2"
                      onClick={() => handleEdit(ec.user_id, "emergency")}
                      disabled={!PERMISSIONS.emergency.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(emergencyInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* DIRECT DEPOSIT */}
      {tab === "directdeposit" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="detail">Institution Name</th>
                <th data-mobile-column="secondary">Account Type</th>
                <th data-mobile-column="secondary">Associate Name</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(directDepositInfos)).map(
                (dd) => (
                  <tr key={dd.user_id}>
                    <td data-mobile-label="User ID" data-mobile-column="id">{dd.userid_no || "-"}</td>
                    <td data-mobile-label="User Name" data-mobile-column="name">{dd.full_name || "-"}</td>
                    <td data-mobile-label="Institution Name" data-mobile-column="detail">{dd.dd_institution_name || "-"}</td>
                    <td data-mobile-label="Account Type" data-mobile-column="secondary">{dd.dd_account_type || "-"}</td>
                    <td data-mobile-label="Associate Name" data-mobile-column="secondary">{dd.dd_associate_name || "-"}</td>
                    <td data-mobile-label="Files" data-mobile-column="secondary">{dd.attachment_count || 0}</td>
                    <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() =>
                          handleEdit(dd.user_id, "directdeposit")
                        }
                        disabled={!PERMISSIONS.directdeposit.edit}
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(directDepositInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* FUEL CARDS */}
      {tab === "fuelcards" && (
        <div className="table-responsive">
          <table data-mobile-kind={tab} data-mobile-layout="compact"
            ref={dashboardTableRef}
            className="table table-hover table-bordered table-striped align-middle"
            data-nw-client-sort="true"
          >
            <thead className="table-dark text-center">
              <tr>
                <th data-mobile-column="id">User ID</th>
                <th data-mobile-column="name">User Name</th>
                <th data-mobile-column="secondary">Card Number</th>
                <th data-mobile-column="detail">Expiry Date</th>
                <th data-mobile-column="secondary">Files</th>
                <th data-mobile-column="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getDashboardPage(applySearchFilter(fuelCardInfos)).map((fc) => (
                <tr key={fc.user_id}>
                  <td data-mobile-label="User ID" data-mobile-column="id">{fc.userid_no || "-"}</td>
                  <td data-mobile-label="User Name" data-mobile-column="name">{fc.full_name || "-"}</td>
                  <td data-mobile-label="Card Number" data-mobile-column="secondary">{fc.card_number || "-"}</td>
                  <td data-mobile-label="Expiry Date" data-mobile-column="detail">
                    {fc.expiry_date
                      ? dayjs(fc.expiry_date).format("YYYY-MM-DD")
                      : "-"}
                  </td>
                  <td data-mobile-label="Files" data-mobile-column="secondary">{fc.attachment_count || 0}</td>
                  <td data-mobile-label="Actions" data-mobile-column="actions" className="text-center">
                    <button
                      className="btn btn-warning btn-sm me-2 btn-action"
                      onClick={() => handleEdit(fc.user_id, "fuel_card")}
                      disabled={!PERMISSIONS.fuelcards.edit}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalItems={applySearchFilter(fuelCardInfos).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

        </div>
      </section>

      {/* Slide-over panels */}
      {slidePanelVisible &&
        (slideDirection === "left" ? (
          <LeftPanelPersonal
            user={selectedUser}
            onClose={() => setSlidePanelVisible(false)}
            onSaved={async (updatedId) => {
              // 1) refresh the table
              await loadUsers();

              // 2) refresh the user shown inside the slide so the header/details update too
              const fresh = await fetchUserDetails(updatedId);
              if (fresh) setSelectedUser(fresh);
            }}
          />
        ) : (
          <>
            <button
              type="button"
              className="right-slide-panel-backdrop"
              aria-label="Close profile preview"
              onClick={() => setSlidePanelVisible(false)}
            />
            <RightPanel
              user={selectedUser}
              onClose={() => setSlidePanelVisible(false)}
            />
          </>
        ))}
    </div>
  );
}

export default AdminDashboard;
